import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useAdminTemplates } from '../../hooks/use-admin-templates.js';
import type { TemplateListParams } from '../../lib/admin-client.js';
import { StatusBadge } from '../shared/status-badge.js';

const styles = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: tokens.colors.textDark,
    margin: 0,
  },
  filters: {
    padding: '0.75rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    width: '180px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontSize: '0.75rem',
    fontWeight: 600,
    color: tokens.colors.textMutedDark,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
    position: 'sticky' as const,
    top: 0,
    backgroundColor: tokens.colors.bgCardDark,
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: tokens.colors.textDark,
    borderBottom: '1px solid rgba(55,65,81,0.5)',
    verticalAlign: 'middle' as const,
  },
  trOdd: {
    backgroundColor: 'rgba(17,24,39,0.3)',
  },
  link: {
    color: tokens.colors.primary,
    textDecoration: 'none',
    fontSize: '0.875rem',
  },
  pagination: {
    padding: '0.75rem 1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${tokens.colors.borderDark}`,
    fontSize: '0.875rem',
    color: tokens.colors.textMutedDark,
  },
  button: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.875rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: tokens.colors.textDark,
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    padding: '0.375rem 0.875rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
};

interface TemplatesListProps {
  onSelect?: (id: string) => void;
  onCreateClick?: () => void;
}

export function TemplatesList({ onSelect, onCreateClick }: TemplatesListProps) {
  const [params, setParams] = useState<TemplateListParams>({
    'page-number': 1,
    'page-size': 20,
    version_status: 'all',
  });

  const { data, isLoading, isError, refetch } = useAdminTemplates(params);

  function handleFilterChange(key: keyof TemplateListParams, value: string) {
    setParams((prev) => ({ ...prev, [key]: value || undefined, 'page-number': 1 }));
  }

  if (isLoading) {
    return (
      <output
        style={{
          display: 'block',
          ...styles.container,
          padding: '2rem',
          textAlign: 'center',
          color: tokens.colors.textMutedDark,
        }}
        aria-label="Loading templates"
      >
        Loading templates…
      </output>
    );
  }

  if (isError) {
    return (
      <div style={{ ...styles.container, padding: '2rem', textAlign: 'center' }} role="alert">
        <p style={{ color: tokens.colors.error }}>Error loading templates</p>
        <button type="button" style={styles.btnSecondary} onClick={() => void refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Templates</h2>
        <button
          type="button"
          style={styles.button}
          onClick={onCreateClick}
          aria-label="Create template"
        >
          + New Template
        </button>
      </div>

      <div style={styles.filters}>
        <input
          style={styles.input}
          placeholder="Filter by code…"
          onChange={(e) => handleFilterChange('code', e.target.value)}
          aria-label="Filter by code"
        />
        <select
          style={styles.input}
          onChange={(e) => handleFilterChange('kind', e.target.value)}
          aria-label="Filter by kind"
        >
          <option value="">All kinds</option>
          <option value="report">Report</option>
          <option value="dashboard">Dashboard</option>
        </select>
        <select
          style={styles.input}
          onChange={(e) => handleFilterChange('version_status', e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="latest">Active</option>
          <option value="deprecated">Deprecated</option>
        </select>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table} aria-label="Templates list">
          <thead>
            <tr>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>Version</th>
              <th style={styles.th}>Kind</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Published</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{ ...styles.td, textAlign: 'center', color: tokens.colors.textMutedDark }}
                >
                  No templates found
                </td>
              </tr>
            ) : (
              items.map((item, idx) => (
                <tr key={item.id} style={idx % 2 === 1 ? styles.trOdd : undefined}>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: tokens.colors.primary,
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                      }}
                      onClick={() => onSelect?.(item.id)}
                    >
                      {item.code}
                    </button>
                  </td>
                  <td style={styles.td}>v{item.version}</td>
                  <td style={styles.td}>{item.kind}</td>
                  <td style={styles.td}>{item.category}</td>
                  <td style={styles.td}>
                    <StatusBadge status={item.status} />
                  </td>
                  <td style={{ ...styles.td, color: tokens.colors.textMutedDark }}>
                    {new Date(item.published_at).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{
                        ...styles.link,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                      onClick={() => onSelect?.(item.id)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && (pagination.total_pages ?? 1) > 1 && (
        <div style={styles.pagination}>
          <span>
            Page {pagination.page_number} of {pagination.total_pages} ({pagination.total_items}{' '}
            total)
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              style={styles.btnSecondary}
              disabled={pagination.page_number <= 1}
              onClick={() =>
                setParams((p) => ({ ...p, 'page-number': (p['page-number'] ?? 1) - 1 }))
              }
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              type="button"
              style={styles.btnSecondary}
              disabled={pagination.page_number >= pagination.total_pages}
              onClick={() =>
                setParams((p) => ({ ...p, 'page-number': (p['page-number'] ?? 1) + 1 }))
              }
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
